import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, FolderPlus, Edit, Github, DownloadCloud } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { GitAccountSelect, RepoSearchSelect, RepoOwnerSelect, RepoAccessNotices, reposForOwner } from './git/RepoPickerParts';
import * as gitSync from '../services/workspaceGitSync';
import { catalogFromList } from '../services/git/repoCatalog';
import teamsService from '../services/teamsService';
import { getProvider } from '../services/git/providers';
import { APP_NAME } from '../utils/appInfo';
import { useVaultStore } from '../store/vaultStore';
import CloneSourceFields, { cloneSources as listCloneSources, vaultItemFor } from './git/CloneSourceFields';
import { requireCloudSync } from '../plans/localGate';
import { newItemsStayOnDevice } from '../plans/cloudAccess';
import { isTauri } from '../utils/isTauri';

const WorkspaceModal = ({
  isOpen,
  onClose,
  onWorkspaceCreated,
  onWorkspaceUpdated,
  onWorkspaceCloned,
  mode: modeProp = 'create',
  workspace = null,
}) => {
  // 'clone' is 'create' started from a repository; only the Git page opens it
  // (git sync card 8 — the Git page is the one place for git).
  const cloneOnly = modeProp === 'clone';
  const mode = cloneOnly ? 'create' : modeProp;
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fork_copy_variables: true,
    workspace_storage: 'synced',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createSource, setCreateSource] = useState('blank');
  const [repoInput, setRepoInput] = useState('');
  const [cloneMode, setCloneMode] = useState('url');
  const [ghConnected, setGhConnected] = useState(false);
  const [repos, setRepos] = useState([]);
  // The user's AND their organisations' repositories (28.10.2026): owners to filter by, notices for hidden orgs.
  const [repoOwners, setRepoOwners] = useState([]);
  const [repoNotices, setRepoNotices] = useState([]);
  const [repoOwner, setRepoOwner] = useState('all');
  const [ghLogin, setGhLogin] = useState('');
  // Teams the user may create a workspace in. The server authorises this again
  // (Authorization.team_can_create_workspace?/2) — this list only decides what
  // to OFFER, so a stale cache cannot grant anything.
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState('');
  const [selectedRepoUrl, setSelectedRepoUrl] = useState('');
  const [showAllRepos, setShowAllRepos] = useState(false);
  // Clone from any host (CloneSourceFields): GitHub keeps the picker below.
  const [cloneSources, setCloneSources] = useState([]);
  const [cloneSourceKey, setCloneSourceKey] = useState('github');
  const [cloneVaultItemId, setCloneVaultItemId] = useState('');
  const cloneSource = cloneSources.find((s) => s.key === cloneSourceKey) || { key: 'github', provider: 'github' };
  const cloneFromGitHub = cloneSource.provider === 'github';

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && workspace) {
        setFormData({
          name: workspace.name || '',
          description: workspace.description || '',
          fork_copy_variables: workspace.settings?.fork_copy_variables !== false,
          workspace_storage: workspace.storage === 'local' ? 'local' : 'synced',
        });
      } else {
        setFormData({
          name: '',
          description: '',
          fork_copy_variables: true,
          // Plans v2 card 3.4: on the desktop a new workspace follows the
          // plan (no cloud sync → this device) and the Settings default.
          workspace_storage: isTauri() && newItemsStayOnDevice() ? 'local' : 'synced',
        });
      }
      setError('');
      setLoading(false);
      setCreateSource(cloneOnly ? 'clone' : 'blank');
      setRepoInput('');
      setCloneMode('url');
      setRepos([]);
      setRepoOwners([]);
      setRepoNotices([]);
      setRepoOwner('all');
      setReposError('');
      setSelectedRepoUrl('');
      setShowAllRepos(false);
    }
  }, [isOpen, mode, cloneOnly, workspace]);

  const loadRepos = async (all = showAllRepos) => {
    setReposLoading(true);
    setReposError('');
    try {
      const catalog = all
        ? gitSync.listUserRepoCatalog
          ? await gitSync.listUserRepoCatalog()
          : catalogFromList(await gitSync.listUserRepos(), { login: ghLogin })
        : catalogFromList(await gitSync.listWorkspaceRepos(), { login: ghLogin });
      setRepos(catalog.repos || []);
      setRepoOwners(catalog.owners || []);
      setRepoNotices(catalog.notices || []);
      setRepoOwner('all');
    } catch (e) {
      setReposError(e?.message || String(e));
    } finally {
      setReposLoading(false);
    }
  };

  const handleToggleShowAll = (e) => {
    const all = e.target.checked;
    setShowAllRepos(all);
    setRepos([]);
    setSelectedRepoUrl('');
    setRepoInput('');
    loadRepos(all);
  };

  useEffect(() => {
    if (!(isOpen && mode === 'create' && createSource === 'clone')) return;
    let cancelled = false;
    setCloneSourceKey('github');
    setCloneVaultItemId('');
    Promise.resolve(useVaultStore.getState().refresh?.()).catch(() => {});
    listCloneSources()
      .then((list) => { if (!cancelled) setCloneSources(list); })
      .catch(() => {});
    (async () => {
      let connected = false;
      try {
        // The browser link or a personal access token — whichever is GitHub on this device.
        const conn = await getProvider('github').getAccount();
        connected = conn.connected;
        if (!cancelled) setGhLogin(conn.login || '');
      } catch {
        connected = false;
      }
      if (cancelled) return;
      setGhConnected(connected);
      if (connected) {
        setCloneMode('repos');
        loadRepos();
      } else {
        setCloneMode('url');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadRepos is redeclared every render; tracking it would refetch the connection and the repo list on every render
  }, [isOpen, mode, createSource]);

  const handleCloneModeChange = (val) => {
    setCloneMode(val);
    setRepoInput('');
    setSelectedRepoUrl('');
    if (val === 'repos' && ghConnected && repos.length === 0 && !reposLoading) {
      loadRepos();
    }
  };

  const handleSelectRepo = (url) => {
    setSelectedRepoUrl(url);
    setRepoInput(url);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (mode === 'create' && createSource === 'clone') {
      if (!repoInput.trim()) {
        setError(t('workspaceModal.repoRequired', 'Enter a repository.'));
        setLoading(false);
        return;
      }
      try {
        const item = cloneFromGitHub ? null : vaultItemFor(cloneVaultItemId);
        if (cloneSource.provider === 'remote' && !item) throw new Error('Choose the Vault item to sign in with.');
        await onWorkspaceCloned({
          repoInput: repoInput.trim(),
          name: formData.name,
          ...(cloneFromGitHub
            ? {}
            : {
                source: {
                  provider: cloneSource.provider,
                  accountId: cloneSource.accountId || undefined,
                  server: cloneSource.server,
                  ...(item ? { vaultItemId: item.id, vaultItemName: item.name } : {}),
                },
              }),
        });
        onClose();
      } catch (err) {
        setError(err.message || t('workspaceModal.failedClone', 'Failed to clone repository'));
      } finally {
        setLoading(false);
      }
      return;
    }

    const submitData =
      mode === 'edit'
        ? {
            name: formData.name,
            description: formData.description,
            settings: {
              fork_copy_variables: formData.fork_copy_variables,
            },
          }
        : {
            name: formData.name,
            description: formData.description,
            type: 'personal',
            visibility: 'private',
            team_ids: [],
            settings: {
              fork_copy_variables: formData.fork_copy_variables,
            },
          };

    if (mode === 'create' && (!user || formData.workspace_storage === 'local')) {
      submitData.storage = 'local';
    }

    // A personal CLOUD workspace needs cloud sync: without it the upgrade
    // opens (card 3.3) instead of a request the server refuses.
    if (mode === 'create' && user && formData.workspace_storage === 'synced' && !requireCloudSync()) {
      setLoading(false);
      onClose();
      return;
    }

    // The server derives `type` from team_id — sending "type" would be ignored,
    // and relying on it would be a client asserting its own authorisation.
    if (mode === 'create' && formData.workspace_storage === 'team') {
      if (!teamId) {
        setError(t('workspaceModal.pickTeam', 'Pick a team for this workspace.'));
        setLoading(false);
        return;
      }
      submitData.team_id = teamId;
      delete submitData.storage;
    }

    try {
      if (mode === 'create') {
        await onWorkspaceCreated(submitData);
      } else {
        await onWorkspaceUpdated(workspace.id, submitData);
      }
      onClose();
    } catch (err) {
      setError(err.message || (mode === 'create' ? t('workspaceModal.failedCreate') : t('workspaceModal.failedUpdate')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || mode !== 'create' || !user) return;
    let cancelled = false;
    teamsService
      .getTeams()
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : list?.teams || [];
        setTeams(rows);
      })
      .catch(() => {
        // A failed lookup hides the option rather than blocking the modal:
        // creating a personal workspace must not depend on the teams call.
        if (!cancelled) setTeams([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, user]);

  if (!isOpen) return null;

  const getModalIcon = () => {
    if (cloneOnly) return <DownloadCloud size={24} className="text-green-500" />;
    if (mode === 'create') {
      return <FolderPlus size={24} className="text-green-500" />;
    }
    return <Edit size={24} className="text-blue-500" />;
  };

  // "team" is only offered when the user is actually in a team they may write
  // to — an option that always errors is worse than an absent one.
  const storageOptions = [
    { value: 'synced', label: t('workspaceModal.storageSynced') },
    { value: 'local', label: t('workspaceModal.storageLocal') },
    ...(teams.length > 0
      ? [{ value: 'team', label: t('workspaceModal.storageTeam', 'Team workspace') }]
      : []),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={cloneOnly ? t('workspaceModal.cloneTitle', 'Clone a repository') : mode === 'create' ? t('workspaceModal.createTitle') : t('workspaceModal.editTitle')}
      subtitle={
        cloneOnly
          ? 'Open an Apiboo workspace that lives in a GitHub repository'
          : mode === 'create'
            ? 'Set up a new workspace for your projects'
            : 'Update workspace settings'
      }
      customIcon={getModalIcon()}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {mode === 'create' && createSource === 'clone' && cloneSources.length > 1 && (
          <div>
            <span className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              {t('workspaceModal.cloneFrom', 'Clone from')}
            </span>
            {/* The app's own dropdown, like "My repositories" below (owner, 04.10.2026). */}
            <GitAccountSelect
              sources={cloneSources}
              value={cloneSourceKey}
              onChange={(key) => {
                setCloneSourceKey(key);
                setRepoInput('');
                setSelectedRepoUrl('');
              }}
              disabled={loading}
              label={t('workspaceModal.cloneFrom', 'Clone from')}
            />
          </div>
        )}

        {mode === 'create' && createSource === 'clone' && !cloneFromGitHub && (
          <CloneSourceFields
            source={cloneSource}
            server={cloneSource.server}
            repoInput={repoInput}
            onRepoInput={setRepoInput}
            vaultItemId={cloneVaultItemId}
            onVaultItem={setCloneVaultItemId}
            disabled={loading}
          />
        )}

        {mode === 'create' && createSource === 'clone' && cloneFromGitHub && (
          <div>
            <label className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              {t('workspaceModal.repoLabel', 'GitHub repository')}
            </label>

            {ghConnected && (
              <div className="modal-picker" style={{ display: 'flex', width: '100%', marginBottom: '0.5rem' }}>
                <CompactSelectPicker
                  value={cloneMode}
                  onChange={handleCloneModeChange}
                  options={[
                    { value: 'repos', label: t('workspaceModal.cloneMyRepos', 'My repositories') },
                    { value: 'url', label: t('workspaceModal.cloneFromUrl', 'Paste URL') },
                  ]}
                  aria-label={t('workspaceModal.repoLabel', 'GitHub repository')}
                />
              </div>
            )}

            {cloneMode === 'repos' && ghConnected ? (
              <div>
                <RepoOwnerSelect
                  owners={repoOwners}
                  value={repoOwner}
                  onChange={setRepoOwner}
                  allText={t('projects.repo.ownerAll')}
                  label={t('projects.repo.owner')}
                  disabled={loading}
                />
                <RepoSearchSelect
                  repos={reposForOwner(repos, repoOwner, selectedRepoUrl)}
                  login={ghLogin}
                  orgText={t('projects.repo.org')}
                  value={selectedRepoUrl}
                  onChange={handleSelectRepo}
                  loading={reposLoading}
                  error={reposError}
                  emptyText={
                    showAllRepos
                      ? t('workspaceModal.repoNone', 'No repositories found.')
                      : t('workspaceModal.repoNoneWorkspacesAction', {
                          app: APP_NAME,
                          defaultValue: `No ${APP_NAME} workspaces found — click to show all repositories`,
                        })
                  }
                  onEmptyAction={
                    showAllRepos
                      ? undefined
                      : () => {
                          setShowAllRepos(true);
                          setSelectedRepoUrl('');
                          setRepoInput('');
                          setRepos([]);
                          loadRepos(true);
                        }
                  }
                  placeholder={t('workspaceModal.repoSelectPlaceholder', 'Select a repository…')}
                  searchPlaceholder={t('workspaceModal.repoSearchPlaceholder', 'Search your repositories…')}
                  onOpen={() => {
                    if (repos.length === 0 && !reposLoading) loadRepos();
                  }}
                  selectedText={t('workspaceModal.repoSelected', 'Selected')}
                />
                {!reposLoading && <RepoAccessNotices notices={repoNotices} ownerKey={repoOwner} t={t} />}
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllRepos}
                    onChange={handleToggleShowAll}
                    className="checkbox-brand cursor-pointer"
                  />
                  <span>
                    {t('workspaceModal.showAllRepos', {
                      app: APP_NAME,
                      defaultValue: `Show all repositories (not just ${APP_NAME} workspaces)`,
                    })}
                  </span>
                </label>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <input
                    type="text"
                    id="repoInput"
                    value={repoInput}
                    onChange={(e) => setRepoInput(e.target.value)}
                    placeholder="owner/repo or https://github.com/owner/repo"
                    className="modal-input"
                    style={{ paddingLeft: '44px' }}
                  />
                  <div
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <Github size={16} className="text-gray-400" />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {ghConnected
                    ? t('workspaceModal.repoHintUrl', 'Paste a github.com URL or owner/repo.')
                    : t(
                        'workspaceModal.repoHint',
                        'Clones an Apiboo workspace from the repo. Sign in to GitHub on the Git page to pick from your own repositories or clone private ones.'
                      )}
                </p>
              </div>
            )}
          </div>
        )}

        <div>
          <label htmlFor="name" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
            {t('workspaceModal.nameLabel')}
          </label>
          <div className="relative">
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required={!(mode === 'create' && createSource === 'clone')}
              placeholder={
                mode === 'create' && createSource === 'clone'
                  ? t('workspaceModal.nameOptionalPlaceholder', 'Optional — defaults to the repo name')
                  : t('workspaceModal.namePlaceholder')
              }
              className="modal-input"
              style={{ paddingLeft: '44px' }}
            />
            <div
              className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
              style={{ top: '50%', transform: 'translateY(-50%)' }}
            >
              <Users size={16} className="text-gray-400" />
            </div>
          </div>
        </div>

        {!(mode === 'create' && createSource === 'clone') && (
          <div>
            <label htmlFor="description" className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300">
              {t('workspaceModal.descriptionLabel')}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('workspaceModal.descriptionPlaceholder')}
              rows={3}
              className="modal-form-textarea"
            />
          </div>
        )}

        {mode === 'create' && createSource === 'blank' && (
          <>
            {user ? (
              <div>
                <label
                  htmlFor="workspace_storage"
                  className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300"
                >
                  {t('workspaceModal.storageLabel')}
                </label>
                <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                  <CompactSelectPicker
                    value={formData.workspace_storage}
                    onChange={(val) =>
                      handleInputChange({ target: { name: 'workspace_storage', value: val, type: 'select' } })
                    }
                    options={storageOptions}
                    aria-label={t('workspaceModal.storageLabel')}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">{t('workspaceModal.storageLocalHint')}</p>

                {/* Appears only for the team option. Directly below the picker
                    rather than in a second step: the team IS the choice — a
                    "team workspace" with no team selected is not a state worth
                    letting the user sit in. */}
                {formData.workspace_storage === 'team' && (
                  <div className="mt-3">
                    <label
                      htmlFor="workspace_team"
                      className="modal-field-label text-sm font-medium text-gray-700 dark:text-zinc-300"
                    >
                      {t('workspaceModal.teamLabel', 'Team')}
                    </label>
                    <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                      <CompactSelectPicker
                        value={teamId}
                        onChange={setTeamId}
                        options={teams.map((team) => ({
                          value: String(team.id),
                          label: team.name,
                        }))}
                        // `teamId` starts empty, and without this the picker
                        // rendered the FIRST team as though it were chosen —
                        // so "Pick a team for this workspace" appeared over a
                        // team the user could see selected. Re-picking that
                        // same team fixed it, which is the tell.
                        placeholder={t('workspaceModal.pickTeamOption', 'Pick a team')}
                        aria-label={t('workspaceModal.teamLabel', 'Team')}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {t(
                        'workspaceModal.teamHint',
                        'Created for the team from the start — no personal copy to share afterwards.'
                      )}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-500">{t('workspaceModal.signedOutLocal')}</p>
            )}
          </>
        )}

        {mode === 'edit' && workspace?.storage === 'local' && (
          <p className="workspace-local-notice">{t('workspaceModal.localEditHint')}</p>
        )}

        {!(mode === 'create' && createSource === 'clone') && (
          <div className="flex items-start space-x-2 pt-1">
            <input
              type="checkbox"
              id="fork_copy_variables"
              name="fork_copy_variables"
              checked={formData.fork_copy_variables}
              onChange={handleInputChange}
              className="checkbox-brand cursor-pointer mt-0.5"
            />
            <label htmlFor="fork_copy_variables" className="text-sm text-gray-700 dark:text-zinc-300 cursor-pointer">
              {t('workspaceModal.forkCopyVariables')}
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || (mode === 'create' && createSource === 'clone' && !repoInput.trim())}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>
                {mode === 'create'
                  ? createSource === 'clone'
                    ? t('workspaceModal.cloning', 'Cloning…')
                    : t('workspaceModal.creating')
                  : t('workspaceModal.updating')}
              </span>
            </>
          ) : (
            <span>
              {mode === 'create'
                ? createSource === 'clone'
                  ? t('workspaceModal.cloneBtn', 'Clone repository')
                  : t('workspaceModal.createBtn')
                : t('workspaceModal.updateBtn')}
            </span>
          )}
        </button>
      </form>
    </Modal>
  );
};

export default WorkspaceModal;
